import React, {Dispatch, FC, SetStateAction, useEffect, useState, useCallback, useRef} from "react";
import {ModalFooter, useModalControls} from "@traas/lib/src/components/layout/ModalProvider";
import {Input} from "../../../lib/src/components/form/Input";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCheckSquare, faChevronDown, faChevronRight, faTimes} from "@fortawesome/free-solid-svg-icons";
import {faPaperclip, faSquare} from "@fortawesome/pro-light-svg-icons";
import {Task} from "@traas/lib/src/api/dto/Task";
import {useApiService} from "@traas/lib/src/api/hooks/useApiService";
import config from "../config";
import {getToken, RichTextContextProvider, RichTextControlBar, RichTextInput} from "@ventumsoftware/react-toolkit";
import {useNavigate} from "react-router";
import {ErrorBag} from "../../../lib/src/api/common/APIService";
import {ValidationError} from "../api/api";
import {EnrichedTeamDayAvailability} from "../planning/components/WeekdayPlanner";
import {InputErrors} from "../components/InputErrors";
import {Button} from "../../../lib/src/components/form/Button";

type TeamEntry = {
  id: string;
  name: string;
  selected: boolean;
};

type EmailEntry = {
  email: string;
  selected: boolean;
};

type EmailsByTeam = {
  [teamId: string]: EmailEntry[];
};
export const SendMailToDailyProjectContactsModal: FC<{
  teamAvailabilities: EnrichedTeamDayAvailability[];
  tasksToday: Task[];
}> = (props) => {
  const {projectService} = useApiService(config, getToken, useNavigate());
  const {close} = useModalControls();

  const [subject, setSubject] = useState("");
  const [content, setContent] = useState("");
  const [teams, setTeams] = useState<TeamEntry[]>(
    Object.values(Object.fromEntries(props.teamAvailabilities.filter(tda => tda.teamId !== 'absence').map((ta) => [ta.team.id, {
      id: ta.team.id,
      name: ta.team.name,
      selected: false,
    }])))
  );

  const [emails, setEmails] = useState<EmailsByTeam | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<ErrorBag>({});

  const [files, setFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null)

  const addFiles = (files: File[]) => {
    setFiles(old => [...old, ...files])
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      addFiles([...e.target.files])
    }
  };

  const fetchEmails = useCallback(async () => {
    setIsLoading(true);

    try {
      const selectedTeams = teams.filter((t) => t.selected);

      const tasksByTeam = props.tasksToday.reduce<Record<string, string[]>>(
        (acc, task) => {
          if (!task.teamId) return acc;

          const isSelected = selectedTeams.some((t) => t.id === task.teamId);
          if (!isSelected) return acc;

          if (!acc[task.teamId]) {
            acc[task.teamId] = [];
          }

          acc[task.teamId].push(task.id);
          return acc;
        },
        {}
      );

      const entries = await Promise.all(
        Object.entries(tasksByTeam).map(async ([teamId, taskIds]) => {
          const result = await projectService.fetchEmailsForProjects(taskIds);

          if (!Array.isArray(result)) {
            throw new Error(`Expected array of emails for team ${teamId}`);
          }

          return [
            teamId,
            result.map((email) => ({
              email,
              selected: true,
            })),
          ] as const;
        })
      );

      setEmails(Object.fromEntries(entries));
    } catch (error) {
      if (error instanceof ValidationError) {
        setErrors(error.errors);
      } else {
        console.error("Unexpected error fetching emails:", error);
      }
    } finally {
      setIsLoading(false);
    }
  }, [teams, props.tasksToday, projectService]);

  const sendEmailsForProjects = async () => {
    if (!emails) return;

    const selectedEmails = Object.values(emails)
      .flat()
      .filter((e) => e.selected)
      .map((e) => e.email);

    if (selectedEmails.length === 0) return;

    try {
      await projectService.sendEmailsForProjects(subject, content, selectedEmails, files);
      close();
    } catch (e) {
      if (e instanceof ValidationError) {
        setErrors(e.errors);
      }
      throw e;
    }
  };

  useEffect(() => {
    if (teams.some((t) => t.selected)) {
      fetchEmails();
    } else {
      setEmails(null);
    }
  }, [teams]);

  return <>
    <div className={"flex flex-col space-y-4"}>
      <div>
        <div className={"flex flex-row items-center text-brand-900 justify-between"}>
        </div>
        {teams.map((team, index) => (
          <TeamRow key={team.id} team={team} index={index} teams={teams} setTeams={setTeams} emails={emails}
                   setEmails={setEmails}/>
        ))}
      </div>

      <InputErrors errors={errors} field={"teams"}/>

      <div>
        <Input type={"text"} label={"Onderwerp"} value={subject} onChange={setSubject}/>
        <InputErrors errors={errors} field={"subject"}/>
      </div>

      <div>
        <div className={'flex flex-col gap-1'}>
          <div className={'flex text-sm font-medium text-brand-900 w-full'}>Email</div>
          <RichTextContextProvider content={content} setContent={setContent}>
            <div className={'min-h-64 flex flex-col p-1 border-2 border-slate-200'}>
              <RichTextControlBar />
              <hr className={'my-1'} />
              <RichTextInput className={'flex-1 focus-within:outline-2 focus-within:outline-brand-200'} />
            </div>
          </RichTextContextProvider>
        </div>
        {/*<Input type={"textarea"} label={"Bericht"} value={content} onChange={setContent}/>*/}
        <InputErrors errors={errors} field={"content"}/>
      </div>
    </div>

    <div className={'pt-2'}>
      <Button type={"secondary"} size={"sm"} text={"Bijlage toevoegen"} icon={faPaperclip}
              onClick={() => fileInputRef.current?.click()}/>
      <input type={'file'} multiple ref={fileInputRef} onChange={handleFileChange} className={'hidden'}
             accept={".doc, .docx, .xls, .xlsx, .ppt, .pptx, .vsd, .vsdx, .pdf, image/*, .gif, .html, .txt, .csv, .zip"}/>
    </div>

    {files.length > 0 && <div className={"px-3 py-2 flex flex-wrap"}>
      {files.map((file, i) => {
        return <button type={'button'} key={i} className={'px-2 py-1 border border-slate-300 rounded-sm text-xs flex items-center justify-between space-x-2 mr-2 mt-2 hover:bg-red-50  hover:text-red-800 '} onClick={() => setFiles(old => old.filter((f, j) => j !== i))}>
          <span>{file.name}</span>
          <FontAwesomeIcon icon={faTimes} />
        </button>
      })}
    </div>}

    <ModalFooter text={"Versturen"} onSubmit={sendEmailsForProjects} disabled={isLoading}/>
  </>
}

const TeamRow: FC<{
  team: TeamEntry;
  index: number;
  teams: TeamEntry[];
  setTeams: Dispatch<SetStateAction<TeamEntry[]>>;
  emails: EmailsByTeam | null;
  setEmails: Dispatch<SetStateAction<EmailsByTeam | null>>;
}> = (props) => {
  const [isCollapsed, setIsCollapsed] = useState<boolean>(true);

  const toggleCheck = (e: React.MouseEvent) => {
    e.stopPropagation();

    const newTeams = [...props.teams];
    newTeams[props.index] = {
      ...props.team,
      selected: !props.team.selected,
    };
    props.setTeams(newTeams);
  };

  const toggleCollapse = () => {
    setIsCollapsed((prev) => !prev);
  };

  const emailCount = props.emails?.[props.team.id]?.length ?? 0;

  return <div className={"mb-2"}>
      <div className={"flex items-center  group"}>
        <div onClick={toggleCheck} className={'hover:bg-brand-50 rounded-sm w-8 h-8 flex items-center cursor-pointer justify-center mr-0.5'}>
          <FontAwesomeIcon icon={props.team.selected ? faCheckSquare : faSquare} className={"text-brand-700"}/>
        </div>
        <div className={"flex items-center justify-between h-8 px-2 w-full hover:bg-brand-50 rounded-sm cursor-pointer"} onClick={toggleCollapse}>
          <div>
            {props.team.name}
            {props.team.selected &&
              <span className={"ml-2 text-sm text-gray-500"}>
                ({emailCount} {emailCount !== 1 ? "contactpersonen" : "contactpersoon"})
              </span>
            }
          </div>
          {props.emails && props.emails?.[props.team.id]?.length > 0 &&
            <div className={"text-sm"}>
              <FontAwesomeIcon icon={isCollapsed ? faChevronRight : faChevronDown} />
            </div>
          }
        </div>
      </div>

      {!isCollapsed && <div className={"ml-8 mt-1 space-y-1"}>
          {props.emails?.[props.team.id]?.map((entry, i) => (
            <EmailRow key={i} entry={entry} index={i} teamId={props.team.id} emails={props.emails} setEmails={props.setEmails}/>
          ))}
        </div>
      }
    </div>
}

const EmailRow: FC<{
  entry: EmailEntry;
  index: number;
  teamId: string;
  emails: EmailsByTeam | null;
  setEmails: Dispatch<SetStateAction<EmailsByTeam | null>>
}> = (props) => {
  const toggleCheck = () => {
    if (!props.emails) return;

    const updated = [...(props.emails[props.teamId] || [])];
    updated[props.index] = {...props.entry, selected: !props.entry.selected};

    props.setEmails({
      ...props.emails,
      [props.teamId]: updated,
    });
  };

  return <div className={"flex items-center space-x-3 h-7 rounded-sm px-1 hover:bg-brand-50 cursor-pointer group"} onClick={toggleCheck}>
    <FontAwesomeIcon icon={props.entry.selected ? faCheckSquare : faSquare} className={'text-brand-700'}/>
    <div>{props.entry.email}</div>
  </div>
}